import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit';
import {
    ConversationMessage,
    SubmitMessageRequest,
    AIConversationSession,
    CreateAIConversationRequest,
    AIConversationResponse,
    AIConversationFilters,
} from '@/services/conversation';

import { get } from 'lodash';

export interface AIConversationState {
    // Sessions
    sessions: AIConversationSession[];
    currentSession: AIConversationSession | null;
    currentSessionId: string | null;

    // Messages
    aiMessage: string;
    messages: ConversationMessage[];
    pinnedMessages: ConversationMessage[];
    suggestedQuestions: string[] | null;

    // Loading states
    isLoading: boolean;
    isCreating: boolean;
    isUpdating: boolean;
    isDeleting: boolean;
    isSending: boolean;
    isUploading: boolean;
    isSearching: boolean;
    isLoadingSuggestions: boolean;
    isLoadingAnalytics: boolean;

    // Error state
    error: string | null;

    // Pagination
    totalCount: number;
    hasNext: boolean;
    hasPrevious: boolean;

    // Timestamps
    lastUpdated: string | null;
}

const initialState: AIConversationState = {
    sessions: [],
    currentSession: null,

    aiMessage: '',
    messages: [],
    pinnedMessages: [],
    currentSessionId: null,
    error: null,
    lastUpdated: null,
    suggestedQuestions: null,

    // Loading states
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isSending: false,
    isUploading: false,
    isSearching: false,
    isLoadingSuggestions: false,
    isLoadingAnalytics: false,

    // Pagination
    totalCount: 0,
    hasNext: false,
    hasPrevious: false,
};
// Fetch list sessions
export const fetchAIConversationSessions = createAction<AIConversationFilters | undefined>(
    'aiConversation/fetchAIConversationSessions',
);
export const fetchAIConversationSessionsSuccess = createAction<AIConversationResponse>(
    'aiConversation/fetchAIConversationSessionsSuccess',
);
export const fetchAIConversationSessionsFailure = createAction<string>(
    'aiConversation/fetchAIConversationSessionsFailure',
);

// Fetch one session
export const fetchAIConversationSession = createAction<string>('aiConversation/fetchAIConversationSession');
export const fetchAIConversationSessionSuccess = createAction<AIConversationSession>(
    'aiConversation/fetchAIConversationSessionSuccess',
);
export const fetchAIConversationSessionFailure = createAction<string>(
    'aiConversation/fetchAIConversationSessionFailure',
);

export const createAIChatSession = createAction<CreateAIConversationRequest>(
    'aiConversationMessage/createAIChatSession',
);
export const createAIChatSessionSuccess = createAction<{ id: string; messages: ConversationMessage[] }>(
    'aiConversationMessage/createAIChatSessionSuccess',
);
export const createAIChatSessionFailure = createAction<string>('aiConversationMessage/createAIChatSessionFailure');

export const createAnonymousAIChatSession = createAction<CreateAIConversationRequest>(
    'aiConversationMessage/createAnonymousAIChatSession',
);
export const createAnonymousAIChatSessionSuccess = createAction<{ id: string; messages: ConversationMessage[] }>(
    'aiConversationMessage/createAnonymousAIChatSessionSuccess',
);
export const createAnonymousAIChatSessionFailure = createAction<string>(
    'aiConversationMessage/createAnonymousAIChatSessionFailure',
);

export const sendMessageWithAI = createAction<SubmitMessageRequest>('aiConversationMessage/sendMessageWithAI');
export const sendMessageWithAISuccess = createAction<{
    user_message: ConversationMessage;
    ai_response: ConversationMessage;
}>('aiConversationMessage/sendMessageWithAISuccess');
export const sendMessageWithAIFailure = createAction<string>('aiConversationMessage/sendMessageWithAIFailure');

export const sendAnonymousMessageWithAI = createAction<SubmitMessageRequest>(
    'aiConversationMessage/sendAnonymousMessageWithAI',
);
export const sendAnonymousMessageWithAISuccess = createAction<{
    user_message: ConversationMessage;
    ai_response: ConversationMessage;
}>('aiConversationMessage/sendAnonymousMessageWithAISuccess');
export const sendAnonymousMessageWithAIFailure = createAction<string>(
    'aiConversationMessage/sendAnonymousMessageWithAIFailure',
);

const aiConversationSessionSlice = createSlice({
    name: 'aiConversationMessage',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        resetState: () => initialState,
        clearMessages: (state) => {
            state.messages = [];
            state.pinnedMessages = [];
        },
        clearAIMessage: (state) => {
            state.aiMessage = '';
        },
        addMessage: (state, action: PayloadAction<ConversationMessage>) => {
            // Check if message already exists
            const existingIndex = state.messages.findIndex((msg) => msg.id === action.payload.id);
            if (existingIndex >= 0) {
                state.messages[existingIndex] = action.payload;
            } else {
                // Insert message in correct sequence order
                const insertIndex = state.messages.findIndex(
                    (msg) => msg.sequence_number > action.payload.sequence_number,
                );
                if (insertIndex >= 0) {
                    state.messages.splice(insertIndex, 0, action.payload);
                } else {
                    state.messages.push(action.payload);
                }
            }
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
            state.isSending = action.payload;
        },
        setSuggestedQuestions: (state, action: PayloadAction<string[]>) => {
            state.suggestedQuestions = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Fetch Sessions
        builder
            .addCase(fetchAIConversationSessions, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchAIConversationSessionsSuccess, (state, action) => {
                state.isLoading = false;
                state.sessions = get(action.payload, 'results', []);
                state.totalCount = get(action.payload, 'count', 0);
                state.hasNext = !!get(action.payload, 'next', null);
                state.hasPrevious = !!get(action.payload, 'previous', null);
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(fetchAIConversationSessionsFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });

        // Fetch Single Session
        builder
            .addCase(fetchAIConversationSession, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchAIConversationSessionSuccess, (state, action) => {
                state.isLoading = false;
                state.currentSession = action.payload;
                state.currentSessionId = action.payload.id;
                state.messages = get(action.payload, 'messages', []);
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(fetchAIConversationSessionFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });

        // Create Session
        builder
            .addCase(createAIChatSession, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createAIChatSessionSuccess, (state, action) => {
                state.isLoading = false;
                state.currentSessionId = action.payload.id;
                state.suggestedQuestions = get(action, 'payload.messages[0].message_data.suggested_questions', []);
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(createAIChatSessionFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });

        // Create Anonymous Session
        builder
            .addCase(createAnonymousAIChatSession, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createAnonymousAIChatSessionSuccess, (state, action) => {
                state.isLoading = false;
                state.currentSessionId = action.payload.id;
                state.suggestedQuestions = get(action, 'payload.messages[0].message_data.suggested_questions', []);
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(createAnonymousAIChatSessionFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });

        // Send Message
        builder
            .addCase(sendMessageWithAI, (state, action) => {
                state.isSending = true;
                state.error = null;
                state.messages.push({
                    id: `${action.payload.content}-${action.payload.session_id}`,
                    content: action.payload.content,
                    message_type: 'user_text',
                    sequence_number: state.messages.length + 1,
                    is_pinned: false,
                    message_data: {},
                    timestamp: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                });
                if (state.messages[state.messages.length - 1].id.startsWith('temp-loading')) {
                    state.messages.pop();
                }
                state.messages.push({
                    id: `temp-loading-${Date.now()}`,
                    content: '...',
                    message_type: 'ai_text',
                    sequence_number: state.messages.length + 1,
                    is_pinned: false,
                    message_data: {},
                    timestamp: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                });
            })
            .addCase(sendMessageWithAISuccess, (state, action) => {
                // Add both user message and AI response
                const { user_message, ai_response } = action.payload;

                state.suggestedQuestions = get(ai_response, 'message_data.suggested_questions', []);
                state.aiMessage = get(ai_response, 'content', '');

                // Add user message
                // const userInsertIndex = state.messages.findIndex(
                //     (msg) => msg.sequence_number > user_message.sequence_number,
                // );
                // if (userInsertIndex >= 0) {
                //     state.messages.splice(userInsertIndex, 0, user_message);
                // } else {
                //     state.messages.push(user_message);
                // }
                state.messages.pop();
                // Add AI response
                const aiInsertIndex = state.messages.findIndex(
                    (msg) => msg.sequence_number > ai_response.sequence_number,
                );
                if (aiInsertIndex >= 0) {
                    state.messages.splice(aiInsertIndex, 0, ai_response);
                } else {
                    state.messages.push(ai_response);
                }

                state.lastUpdated = new Date().toISOString();
                state.error = null;
                state.isSending = false;
            })
            .addCase(sendMessageWithAIFailure, (state, action) => {
                state.messages.pop();
                state.isSending = false;
                state.error = action.payload;
            });

        // Send Anonymous Message
        builder
            .addCase(sendAnonymousMessageWithAI, (state, action) => {
                state.isSending = true;
                state.error = null;
                state.messages.push({
                    id: `${action.payload.content}-${action.payload.session_id}-${Date.now()}`,
                    content: action.payload.content,
                    message_type: 'user_text',
                    sequence_number: state.messages.length + 1,
                    is_pinned: false,
                    message_data: {},
                    timestamp: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                });
                if (state.messages[state.messages.length - 1].id.startsWith('temp-loading')) {
                    state.messages.pop();
                }
                state.messages.push({
                    id: `temp-loading-${Date.now()}`,
                    content: '...',
                    message_type: 'ai_text',
                    sequence_number: state.messages.length + 1,
                    is_pinned: false,
                    message_data: {},
                    timestamp: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                });
            })
            .addCase(sendAnonymousMessageWithAISuccess, (state, action) => {
                // Add both user message and AI response
                const { ai_response } = action.payload;

                state.messages.pop();
                // Add AI response
                state.suggestedQuestions = get(ai_response, 'message_data.suggested_questions', []);
                const aiInsertIndex = state.messages.findIndex(
                    (msg) => msg.sequence_number > ai_response.sequence_number,
                );
                if (aiInsertIndex >= 0) {
                    state.messages.splice(aiInsertIndex, 0, ai_response);
                } else {
                    state.messages.push(ai_response);
                }

                state.lastUpdated = new Date().toISOString();
                state.error = null;
                state.isSending = false;
            })
            .addCase(sendAnonymousMessageWithAIFailure, (state, action) => {
                state.messages.pop();
                state.isSending = false;
                state.error = action.payload;
            });
    },
});

export const { clearError, resetState, clearMessages, addMessage, clearAIMessage, setLoading, setSuggestedQuestions } =
    aiConversationSessionSlice.actions;

export default aiConversationSessionSlice.reducer;
